import React, { useEffect, useState } from "react";
import { Outlet, useNavigate, useParams } from "react-router-dom";
import BuilderModal from "../../Layouts/Modal/BuilderModal";
import Navbar from "../../Layouts/Navbar/Navbar";
import fileFolderConfig, { getFileInfo } from "../../commonFunctions/fileFolderConfig";
import FilterApplicationBlocks from "../../components/BuilderComponents/FilterApplicationBlocks";
import LeftSideBar, { FolderCollapse } from "../../components/SideBar/LeftSideBar";
import { useAppBlocksContext } from "../../context/AppBlocksContext";
import axiosInstance, { handleApiError } from "../../services/axiosService";
import { toastSuccess } from "../../utils/toastUtils";
import { useModalContext } from "../../context/ModalContext";
import { useTableContext } from "../../context/TableContext";
import { useTreeContext } from "../../context/TreeDataContext";
import "./ApplicationBlock.css";
import ModalOfApplicationBlockCreation from "./ModalOfApplicationBlockCreation";
import { useNavigationContext } from "../../context/NavigationContext";
import { useScreenContext } from "../../context/ScreenContext";
import { TryAgainMessage } from "../Screen/ScreenSection";
import useLocalStorage from "../../hooks/useLocalStorage";
import ResizableComponent from "../../Layouts/ResizableComponent";
import HeaderModes from "../../Layouts/Navbar/HeaderModes";
import { filterBlocksByFileInfo } from "../Screen/ScreenElementsRightBar";
import { deleteFolderOfFile, editFolderOrFile, insertNewFileOrFolder } from "../../utils/treeData/folderEditorUtils";

const ApplicationBlockSection = () => {
  const { blocksExplorerData, setBlocksExplorerData, selectedDevice } = useTreeContext();
  const { selectedAppBlock, setSelectedAppBlock, fetchAllApplicationBlock } = useAppBlocksContext();
  const { appId } = useParams();
  const [checkedBlocks, setCheckedBlocks] = useLocalStorage("application_block_filters_" + appId, {
    table: [],
    tag: [],
    appUser: false,
    rdbms: false
  });
  const { getUserConfirmationForDelete } = useModalContext();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [insertPopupData, setInsertPopupData] = useState({});
  const { getAllTableData, getAllRdbmsData, getAllAppuserData } = useTableContext();
  const [newBlockData, setNewBlockData] = useState(null);
  const { saveChangesAndNavigate } = useNavigationContext();
  const { fetchAllScreens } = useScreenContext();

  const { blockId } = useParams();

  const [isLoader, setIsLoader] = useState(true);
  const [isError, setIsError] = useState(false);
  const navigate = useNavigate();

  const insertDataAndUpdateTree = async (folderId, config) => {
    try {
      const endPoint = folderId.name;
      const { data, message } = await axiosInstance.post(`/${endPoint}`, config);
      const newItem = fileFolderConfig.getFileConfig(
        data.id,
        data.name,
        folderId.parentData + "/" + folderId.name,
        true,
        true,
        getFileInfo(folderId.name, config),
        data.table_type
      );
      const newTree = insertNewFileOrFolder(blocksExplorerData, folderId, newItem);
      setBlocksExplorerData([...newTree]);
      navigate(`./${data.id}`);
      setIsModalOpen(false);
      toastSuccess(message);
    } catch (error) {
      handleApiError(error);
    }
  };

  const handleNewInsert = async (folderId, name) => {
    // if (folderId.insert_type === "popup" || name.trim().length === 0) return;
    if (folderId.insert_type === "popup") {
      setIsModalOpen(true);
      setInsertPopupData(folderId);
      return;
    }
    if (name.trim().length === 0) return;
    const config = {
      name: name,
      device: folderId.parentData
    };
    insertDataAndUpdateTree(folderId, config);
  };

  const handleDelete = async (folder) => {
    try {
      const userConfirmed = await getUserConfirmationForDelete(folder.name);
      if (!userConfirmed) return;
      const block = folder.parentData.split("/")[1];
      const { message } = await axiosInstance.delete(`/${block}/${folder.id}`);
      const newTree = deleteFolderOfFile(blocksExplorerData, folder);
      setBlocksExplorerData(newTree);
      if (folder.id === selectedAppBlock?.id) {
        navigate(`./`);
      }

      toastSuccess(message);
    } catch (error) {
      handleApiError(error);
    }
  };

  const handleEdit = async (folder, newName) => {
    try {
      const config = {
        name: newName
      };
      const block = folder.parentData.split("/")[1];
      const { message } = await axiosInstance.put(`/${block}/${folder.id}`, config);
      const newTree = editFolderOrFile(blocksExplorerData, folder, newName);
      setBlocksExplorerData([...newTree]);
      toastSuccess(message);
    } catch (error) {
      handleApiError(error);
    }
  };

  const callbackFunction = (id) => {
    if (selectedAppBlock.id === id) return;
    // navigate(`./${id}`);
    saveChangesAndNavigate(id);
  };

  const initialFetching = async () => {
    setIsLoader(true);
    setIsError(false);
    try {
      await getAllTableData();
      await getAllRdbmsData();
      await getAllAppuserData();
      await fetchAllScreens();
      if (blocksExplorerData && blocksExplorerData.length > 0) {
        setBlocksExplorerData(blocksExplorerData);
        if (selectedAppBlock.id && !blockId) {
          setSelectedAppBlock({ id: "" });
        }
        handleBlockFilter();
      } else {
        const { treeData } = await fetchAllApplicationBlock();
        handleBlockFilter(treeData);
      }
    } catch (error) {
      handleApiError(error);
      setIsError(true);
    } finally {
      setIsLoader(false);
    }
  };

  useEffect(() => {
    initialFetching();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleBlockFilter = (data = blocksExplorerData) => {
    const blockCopy = JSON.parse(JSON.stringify(data));
    const index = blockCopy.findIndex((block) => block.id === selectedDevice);
    if (index === -1 || !Array.isArray(blockCopy[index].items)) {
      console.warn(`No valid block found for device: ${selectedDevice}`);
      return;
    }
    const filteredItems = filterBlocksByFileInfo(blockCopy[index].items, checkedBlocks);
    blockCopy[index].items = filteredItems;
    setNewBlockData(blockCopy);
  };

  useEffect(() => {
    if (!blocksExplorerData) return;
    handleBlockFilter();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkedBlocks, blocksExplorerData, selectedDevice]);

  return (
    <>
      <section className="main_section">
        <div className="sub_section">
          {isError ? (
            <TryAgainMessage retryFunction={initialFetching} />
          ) : (
            <>
              <ResizableComponent
                resizeSide="right"
                storageKey="left_sidebar_size"
                defaultSize={244}
                range={[244, 400]} // Specify the range as [minSize, maxSize]
                collapsible={true}
              >
                <HeaderModes />
                <div className="d_flex">
                  <Navbar />
                  <LeftSideBar
                    explorerData={newBlockData}
                    setExplorerData={setNewBlockData}
                    selectedScreen={selectedAppBlock}
                    callbackFunction={callbackFunction}
                    handleNewInsert={handleNewInsert}
                    handleDelete={handleDelete}
                    handleEdit={handleEdit}
                    type="Apps Block"
                    isLoader={isLoader}
                  >
                    <div className="d_flex align_center gap_2">
                      <FolderCollapse explorerData={newBlockData} setExplorerData={setNewBlockData} type="blocks" />
                    </div>
                    <FilterApplicationBlocks
                      checkedBlocks={checkedBlocks}
                      setCheckedBlocks={setCheckedBlocks}
                      customStyles={{ right: "8px", width: "160px", paddingLeft: "4px", paddingRight: "4px" }}
                    />
                  </LeftSideBar>
                </div>
              </ResizableComponent>
              {!isLoader && <Outlet />}
            </>
          )}
        </div>
        <BuilderModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          modalName={`Add ${insertPopupData.name}`}
        >
          <ModalOfApplicationBlockCreation
            insertPopupData={insertPopupData}
            insertDataAndUpdateTree={insertDataAndUpdateTree}
          />
        </BuilderModal>
      </section>
    </>
  );
};

export default ApplicationBlockSection;
